import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { ROUTES } from 'routing';

if (process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENVIRONMENT,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    integrations: [new Sentry.BrowserTracing()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.2,
  });
}

// Browser translators and extensions (e.g. Google Translate) replace text nodes React owns,
// so React later fails with "removeChild/insertBefore: not a child of this node" and the whole screen crashes.
// Skip those operations instead of throwing. See https://github.com/facebook/react/issues/11538
const originalRemoveChild = Node.prototype.removeChild;
Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
  if (child.parentNode !== this) {
    console.warn('Cannot remove a child from a different parent', child, this);
    return child;
  }
  return originalRemoveChild.call(this, child) as T;
};
const originalInsertBefore = Node.prototype.insertBefore;
Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
  if (referenceNode && referenceNode.parentNode !== this) {
    console.warn('Cannot insert before a reference node from a different parent', referenceNode, this);
    return newNode;
  }
  return originalInsertBefore.call(this, newNode, referenceNode) as T;
};

// Simple error handler for recursion issues
window.addEventListener('error', (event) => {
  if (event.error?.message?.includes('Maximum call stack size exceeded')) {
    // Send to Sentry with enhanced context
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'recursion');
      scope.setContext('recursion_error', {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        stack: event.error.stack,
      });
      Sentry.captureException(event.error);
    });
  }
});

const container = document.getElementById('root');
const root = createRoot(container!);
let router = createBrowserRouter(ROUTES);
root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/firebase-messaging-sw.js`).then(
    () => {},
    (error) => {
      Sentry.captureException(error);
      console.error(`Service worker registration failed: ${error}`);
    },
  );
} else {
  console.error('Service workers are not supported.');
}

export { router };
